import React, { useEffect, useMemo, useState } from 'react'
import { Flex, Icon, Text, Input, InputGroup, Button, InputRightElement } from '@chakra-ui/react'
import SingleSelect from "../../reports/components/SingleSelect"
import CustomModal from '../../reports/pages/components/CustomModal'
import { useForm } from 'react-hook-form'
import { AUTOMATION_TYPES } from '../../../utils/consts'
import SocketIcon from '../../../assets/icons/Socket'
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import WorkingDaysSelector from './WorkingDaysSelector'
import EmailInput from '../../reports/pages/components/EmailInput'
import { toast } from 'react-toastify'
import { useGetAllPhysicalDevicesQuery } from '../../alarms/api/alarmsApi'
import { comparisonsArr } from '../../analysis/utils'
import { useGetDeviceTopicCommandQuery } from '../api/automationsApi'
import { useGetParametersQuery } from '../../analysis/api/analysisApi'

interface RegisterAutomationProps {
    isOpen: boolean
    automationType: string
    handleClose: () => void
    deviceToControl?: any
    automationToEdit?: any
    deviceParam?: any
    createAutomation?: (body: any) => any
    updateAutomation: (body: any) => any
}

export default function RegisterAutomationModal({ isOpen, handleClose, deviceToControl, automationToEdit, automationType, deviceParam, createAutomation, updateAutomation }: RegisterAutomationProps) {
    const [step, setStep] = useState(1)
    const [emails, setEmails] = useState([])
    const [timeRange, setTimeRange] = useState({ from: "" })

    const isEditMode = !!automationToEdit
    const device = deviceToControl || automationToEdit?.device || automationToEdit
    const { data: commandsData, isLoading } = useGetDeviceTopicCommandQuery({ topic: deviceParam?.id || device?.group_topics|| automationToEdit?.command?.channel_topic?.id}, { skip: !isOpen })
   
    const parametersSelect = useMemo(() => {
  
        return commandsData?.map(({ id, payload_override, command }) => ({
            id,
            name: `${command?.description || command?.name} ${payload_override}` ,
            key: command.key
        }))
    }, [device, commandsData])

    const {
        handleSubmit,
        watch,
        setValue,
        register,
        reset,
        formState: { errors },
    } = useForm()

    useEffect(() => {
        setStep(1)
    }, [isOpen])

    useEffect(() => {
        if (isOpen) {
            setStep(1)

            if (isEditMode) {
                setValue('automationName', automationToEdit.name)
                setValue('selectedAutomationType', automationToEdit.type === 'device' ? 1 : 2)
                setValue('selectedCommand', commandsData?.find((param) => param.id === automationToEdit?.command?.id).id) // this may need to be ajusted and for the groups
                setValue('value', automationToEdit.value?.status)
                setValue('radioValue', automationToEdit.value?.status)
                setValue('dimmerValue', automationToEdit.value?.brightness)
                setValue('workingDays', automationToEdit.working_days || [])
                setTimeRange(automationToEdit.working_hours || { from: "" })
                setEmails(automationToEdit.emails || [])

                if (Object.keys(automationToEdit?.config)?.length) {
                    const rule = automationToEdit.config.conditions.groups[0].rules[0]
                    setValue('selectedDeviceControl', rule.device_id)
                    setValue('selectedDeviceControlParameter', rule.parameter)
                    setValue('value_comparison', rule.operator)
                    setValue('deviceControlValue', rule.value)
                    setValue('radioDeviceControlValue', rule.value)
                    setValue('selectedDeviceControlMeasurementType', rule.measurement_type_id)
                }
            }
        }
    }, [isOpen, isLoading])


    const value = watch('value') || null
    const dimmerValue = watch('dimmerValue') || null
    const radioValue = watch('radioValue') || null
    const radioDeviceControlValue = watch('radioDeviceControlValue') || null
    const value_comparison = watch('value_comparison') || null
    const deviceControlValue = watch('deviceControlValue') || null
    const selectedAutomationType = watch('selectedAutomationType') || null
    const selectedCommand = watch('selectedCommand')
    const selectedDeviceControl = watch('selectedDeviceControl') || null
    const selectedDeviceControlMeasurementType = watch('selectedDeviceControlMeasurementType') || null

    const { data: userPhysDevices } = useGetAllPhysicalDevicesQuery(
        { skip: !isOpen },
    )

    const { data: deviceControlMeasurementTypes } = useGetParametersQuery({ exclude_measurement_unit: ["Wh", "VArh"], device: selectedDeviceControl }, { skip: !isOpen || !selectedDeviceControl })


    const setSelectedAutomationType = (selectedAutomationType: number) => {
        setValue('selectedAutomationType', selectedAutomationType)
    }
    const setSelectedParameter = (selectedCommand: string) => {
        setValue('selectedCommand', selectedCommand)
        setValue("radioValue", null)
        setValue("value", null)
    }
    const setSelectedDeviceControl = (selectedDeviceControl: string) => {
        setValue('selectedDeviceControl', selectedDeviceControl)
    }
    const setSelectedDeviceControlMeasurementType = (
        selectedDeviceControlMeasurementType: number,
    ) => {
        setValue('selectedDeviceControlMeasurementType', selectedDeviceControlMeasurementType)
    }


    const resetFields = () => {
        reset()
        setValue("radioValue", null)
        setValue("value", null)
        setValue("selectedCommand", null)
        setValue("selectedAutomationType", null)
        setValue("workingDays", null)
        setValue("selectedDeviceControl", null)
        setValue("selectedDeviceControlMeasurementType", null)
        setValue("value_comparison", null)
        setValue("deviceControlValue", null)
        setValue("dimmerValue", null)
        setTimeRange({ from: "" })
        setEmails([])
    }

    const handleCloseModal = () => {
        resetFields()
        handleClose()
    }

    const handleFieldChange = (field, value) => {
        if (value !== "") {
            let newValue = Number(value)

            if (newValue > 100) {
                newValue = 100
            }
            setValue(field, newValue)
        } else {
            setValue(field, null)
        }
    }

    const handleNext = () => setStep((prev) => prev + selectedAutomationType)
    const handlePrevious = () => setStep((prev) => prev - selectedAutomationType)

    const handleTimeChange = (e) => {
        setTimeRange({ ...timeRange, [e.target.name]: e.target.value });
    }

    const handleInputClick = (e) => {
        e.target.showPicker()
    }

    const alarmTypesSelect = AUTOMATION_TYPES?.map(({ id, name }) => ({
        id,
        name,
    }))

    const deviceControl = userPhysDevices?.find((device) => device.id === selectedDeviceControl)

    const userDevicesSelect = useMemo(
        () => {
            return userPhysDevices?.map(({ id, title }) => ({
                id,
                name: title,
            }))
        },
        [userPhysDevices]
    )


    const parameterKey = parametersSelect?.find((obj) => obj.id === selectedCommand)?.key

    const deviceControlMeasurementTypesSelect = useMemo(
        () => {
            return deviceControlMeasurementTypes?.map(({ id, title, parameters, measurement_unit }) => ({
                id,
                name: title,
                parameters,
                measurement_unit
            }))
        },
        [deviceControlMeasurementTypes]
    )
    const parameterMeasurementUnit = useMemo(
        () => {
            return deviceControlMeasurementTypesSelect?.find(({ id }) => id === selectedDeviceControlMeasurementType)?.measurement_unit?.title
        }, [selectedDeviceControlMeasurementType]
    )
    const renderStepOne = () => (
        <>
            <Flex align="center" gap="2">
                <Icon as={SocketIcon} boxSize={5} />
                <Text fontSize="lg">
                    {device?.title || device?.name || device?.group_info?.name}
                </Text>
            </Flex>
            <SingleSelect
                key="automationType"
                items={alarmTypesSelect}
                isDisabled={false}
                {...register('selectedAutomationType')}
                setSelectedOption={setSelectedAutomationType}
                selectedOption={selectedAutomationType}
                required={selectedAutomationType === null}
                header="Select automation type"
            />

            <Flex justify="space-between" gap="2" align="center">
                <SingleSelect
                    key="parameters"
                    items={parametersSelect}
                    isDisabled={false}
                    {...register('selectedCommand')}
                    setSelectedOption={setSelectedParameter}
                    selectedOption={selectedCommand}
                    required={!selectedCommand}
                    header="Select command"
                />
                {parameterKey && parameterKey?.includes("brightness") && (
                    <>
                        <Text>-</Text>
                        <InputGroup>
                            <InputRightElement
                                zIndex="0"
                                height="100%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                pointerEvents="none"
                            >
                                <Text>%</Text>
                            </InputRightElement>
                            <Input
                                height="48px"
                                placeholder="Value"
                                {...register('dimmerValue')}
                                onChange={(e) => {
                                    const brightnessValue = e.target.value === "" ? "" : Number(e.target.value)
                                    handleFieldChange("dimmerValue", brightnessValue)
                                }}
                                type="number"
                                min={0}
                                max={100}
                                borderColor='border.strong'
                            />
                        </InputGroup>

                    </>
                )}
            </Flex>

        </>
    )

    const renderStepTwo = () => (
        <>
            <SingleSelect
                key="deviceControl"
                items={userDevicesSelect}
                isDisabled={false}
                {...register('selectedDeviceControl')}
                setSelectedOption={setSelectedDeviceControl}
                selectedOption={selectedDeviceControl}
                required={selectedDeviceControl === null}
                header="Select device to read from"
            />
            {selectedDeviceControl && (
                <>
                    <SingleSelect
                        key="device-control-measurement-types"
                        items={deviceControlMeasurementTypesSelect}
                        isDisabled={false}
                        {...register('selectedDeviceControlMeasurementType')}
                        setSelectedOption={setSelectedDeviceControlMeasurementType}
                        selectedOption={selectedDeviceControlMeasurementType}
                        required={selectedDeviceControlMeasurementType === null}
                        header="Select device measurement type"
                    />

                    {parameterMeasurementUnit?.toLowerCase() !== 'bool' ? ( // here  we need to know if the parameter have boolean field to show only on / off device need to be handle and in the backend 
                        <>
                            <Flex justify="space-between" gap="2" align="center">
                                <SingleSelect
                                    header="Select a comparison"
                                    items={comparisonsArr}
                                    required={value_comparison === null}
                                    setSelectedOption={(selectedOptionId: string) =>
                                        setValue('value_comparison', selectedOptionId)
                                    }
                                    selectedOption={value_comparison}
                                />
                                <Text>-</Text>
                                <InputGroup width="30%">
                                    <Input
                                        {...register('deviceControlValue')}
                                        type="number"
                                        placeholder="Value"
                                        borderColor={deviceControlValue !== null ? 'border.strong' : 'red.500'}
                                    />
                                    <InputRightElement pointerEvents="none" color="gray.500" fontSize="sm">
                                        {parameterMeasurementUnit}
                                    </InputRightElement>
                                </InputGroup>

                            </Flex>
                        </>
                    ) :
                        <Flex justify="space-between" gap="2" align="center">
                            <SingleSelect
                                key="radio-device-control-options"
                                items={[
                                    { name: 'Off', id: "0" },
                                    { name: 'On', id: "1" },
                                ]}
                                setSelectedOption={(val) => {
                                    setValue('radioDeviceControlValue', val)
                                }}
                                selectedOption={radioDeviceControlValue}
                                required={!radioDeviceControlValue}
                                header="Select action"
                            />
                        </Flex>}
                </>
            )}
        </>
    )

    const renderStepThree = () => (
        <>
            <Flex direction="column" width="100%">
                <Text fontSize="13px" color="text.dark">
                    Working days
                </Text>
                <WorkingDaysSelector
                    selectedDays={watch('workingDays') || []}
                    onChange={(days) => setValue('workingDays', days)}
                    isRequired
                />
            </Flex>
            {selectedAutomationType === 2 && <Input
                type="time"
                name="from"
                value={timeRange.from}
                onChange={handleTimeChange}
                onClick={handleInputClick}
                size="lg"
                borderColor={timeRange.from ? "gray.300" : "red.500"}
                focusBorderColor="blue.500"
                _hover={{
                    cursor: "pointer",
                }}
            />}
            <Input
                w="full"
                bg="white"
                border="1px"
                height="48px"
                borderColor={watch('automationName') ? 'border.strong' : 'red.500'}
                textAlign="left"
                fontSize="18px"
                fontWeight="normal"
                id='automationName'
                {...register('automationName', { required: true })}
                placeholder="Enter automation name"
            />
            <EmailInput emails={emails} setEmails={setEmails} />
        </>
    )

    const isStepOneComplete = () => {
        return (
            !selectedAutomationType ||
            !selectedCommand ||
            (parameterKey?.includes("brightness") && !dimmerValue)
        )
    }

    const isStepTwoComplete = () => {
        return (
            !selectedDeviceControl ||
            !selectedDeviceControlMeasurementType ||
            (parameterMeasurementUnit?.toLowerCase() !== 'bool' && !value_comparison) ||
            (parameterMeasurementUnit?.toLowerCase() !== 'bool' && !deviceControlValue) ||
            (parameterMeasurementUnit?.toLowerCase() === 'bool' && !radioDeviceControlValue)
        )
    }

    const isStepThreeComplete = () => {
        return (
            !watch('workingDays') ||
            (selectedAutomationType === 2 && !timeRange?.from) ||
            !watch('automationName')
        )
    }

    const isCreateDisabled = () => {
        if (selectedAutomationType === 1) {
            return isStepOneComplete() || isStepTwoComplete() || isStepThreeComplete()
        } else {
            return isStepOneComplete() || isStepThreeComplete()
        }
    }

    const isNextDisabled = () => {
        if (selectedAutomationType === 1) {
            return ((step === 1 && isStepOneComplete()) || (step === 2 && isStepTwoComplete())) || step === 3
        } else {
            return (step === 1 && isStepOneComplete()) || step === 3
        }
    }

    const handleSubmitAutomation = async () => {
        const toastId = toast.info(isEditMode ? 'Updating automation...' : 'Creating automation...', {
            style: { padding: '10px 20px' },
            autoClose: false,
            isLoading: true,
            progress: undefined,
        })

        let requestParams: any = {
            name: watch('automationName'),
            type: selectedAutomationType === 1 ? "device" : "time",
            emails,
            working_days: watch('workingDays'),
            working_hours: timeRange,
            command: selectedCommand,
        }

        if (automationType === 'device') {
            requestParams.device = device.id
        }

        if (automationType === 'group' && !isEditMode) {
            requestParams.group = device.id
        }

        if (parameterKey?.includes("switch")) {
            requestParams.value = {
                status: radioValue || value || '',
            }
        } else if (parameterKey?.includes("light")) {
            requestParams.value = {
                status: radioValue || value || '',
                brightness: dimmerValue,
            }
        }

        if (selectedAutomationType === 1) {
            requestParams.config = {
                conditions:
                {
                    operator: 'OR',
                    groups: [
                        {
                            operator: 'OR',
                            rules: [
                                {
                                    device_id: selectedDeviceControl,
                                    device_name: deviceControl?.title,
                                    parameter: deviceControlMeasurementTypesSelect?.find(({ id }) => id === selectedDeviceControlMeasurementType)?.name,
                                    operator: value_comparison || '==',
                                    value: deviceControlValue || radioDeviceControlValue,
                                    measurement_type_id: selectedDeviceControlMeasurementType,
                                    measurement_unit: parameterMeasurementUnit
                                },
                            ],
                        },
                    ],
                },
            }
        }

        const request = isEditMode
            ? updateAutomation({ id: automationToEdit.id, body: requestParams })
            : createAutomation(requestParams)

        request
            .then((response) => {
                toast.update(toastId, {
                    render: isEditMode ? "Successfully updated" : "Successfully created",
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                })
                handleCloseModal()
            })
            .catch(() => {
                toast.update(toastId, {
                    render: isEditMode ? "Failed to update automation!" : "Failed to create automation!",
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                })
                handleCloseModal()
            })
    }

    return (
        <CustomModal
            title={`${isEditMode ? "Edit" : "New"} automation`}
            tooltipLabel="Please populate the required fields first"
            handleSend={handleSubmit(() => handleSubmitAutomation())}
            isDisabled={isCreateDisabled()}
            isLoading={isLoading}
            isOpen={isOpen}
            onClose={handleCloseModal}
            buttonName={isEditMode ? "Edit" : "Create"}
        >
            <>
                <Flex direction="column" gap="20px">
                    {step === 1 && renderStepOne()}
                    {step === 2 && renderStepTwo()}
                    {step === 3 && renderStepThree()}
                </Flex>
                <Flex justify="space-between" mt="4">
                    <Button
                        onClick={handlePrevious}
                        isDisabled={step === 1}
                        aria-label="Previous"
                    >
                        <ArrowBackIcon />
                    </Button>
                    <Button
                        onClick={handleNext}
                        colorScheme="blue"
                        isDisabled={isNextDisabled()}
                        aria-label="Next"
                    >
                        <ArrowForwardIcon />
                    </Button>
                </Flex>
            </>
        </CustomModal>
    )
}
